import { Component, Input, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormBuilder,
  FormControl,
} from '@angular/forms';
import { ActivatedRoute, } from '@angular/router';
import {
  PhoneNumber,
} from '@modules/ota/become-agency/models/property-info.model';
import { InputPhoneNumberComponent } from '@shared/components/input-phone-number/input-phone-number.component';

import { MessageService } from 'primeng/api';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { ImagesService } from '@modules/my-booking/services/image/media.service';
import { PropertyManagementService } from '@modules/ota/hotel-ota/services/property-management/property-management.service';

interface Rule {
  name: string;
  key: string;
  icon: string;
}

@Component({
  selector: 'app-contract-info-legality-review-v2',
  templateUrl: './contract-info-legality-review.component.html',
  styleUrls: ['./contract-info-legality-review.component.scss'],
  providers: [MessageService, PropertyManagementService],
})
export class ContractInfoLegalityReviewComponentV2 {
  @ViewChild('phoneNumberRef') phoneNumberChild: InputPhoneNumberComponent;

  @Input() data: any;
  @Input() isFormDisabled: boolean;

  // Regex
  public contractAgreementForm: FormGroup;
  public rules: Rule[];
  phoneNumber: PhoneNumber;

  dragAreaClass: string = '';
  isEditVisible: boolean = true;
  loadingFileFromLocal: boolean = false;
  draggedFiles: any[] = [];
  selectedRuleModel: Rule;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private imagesService: ImagesService,
    private messageService: MessageService,
    private propertyManagementService: PropertyManagementService
  ) {}

  ngOnInit(): void {
    this.initFormBuilder();
    this.contractAgreementForm.markAsPristine();
    this.rules = [
      {
        name: 'OTA.CONTRACT_AGREEMENT.BUSINESS',
        key: '0',
        icon: 'sctr-icon-building-03',
      },
      {
        name: 'OTA.CONTRACT_AGREEMENT.PERSONAL',
        key: '1',
        icon: 'sctr-icon-user-01',
      },
    ];
    this.renderValueContractForm(this.data);
    this.phoneNumber = this.data.phone;
    this.renderAttachment(this.data);
  }

  downloadImage(url: string, filename: string) {
    this.imagesService.downloadImage(url, filename);
  }

  getSubstringAfterFirstDotFromEnd(inputString: string): string {
    const index = inputString.lastIndexOf('.');
    if (index === -1) return inputString;
    return inputString.substring(index + 1);
  }

  previewImg(file: any) {
    if (this.isFormDisabled) {
      let baseURL = environment.BASE_PATH;
      let url = this.getSubstringAfterFirstDotFromEnd(file.original_name);
      let downloadFile = `${baseURL}/storage/files/download/${
        file.id + '.' + url
      }`;
      this.imagesService.downloadImage(downloadFile, file.original_name);
    }
  }

  downloadAllFiles(files: any | any[]) {
    const baseURL = environment.BASE_PATH;

    const fileList = Array.isArray(files) ? files : [files];

    fileList.forEach((file: any) => {
      const extension = this.getSubstringAfterFirstDotFromEnd(
        file.file?.name || file.original_name
      );
      const fileName = file.file?.name || file.original_name || 'unknown_file';
      const fileId = file.id;
      const downloadFile = `${baseURL}/storage/files/download/${fileId}.${extension}`;

      this.imagesService.downloadImage(downloadFile, fileName);
    });
  }

  isFileTypeAllowed(fileType: string): boolean {
    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/svg+xml',
      'image/webp',
    ];
    return allowedMimeTypes.includes(fileType);
  }

  showError(detail: string) {
    this.messageService.add({
      key: 'FILE_ERROR',
      severity: 'error',
      detail: detail,
    });
  }

  handleDownload(item: { file: File }) {
    const url = URL.createObjectURL(item.file);
    const link = document.createElement('a');
    link.href = url;
    link.download = item.file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Xoá URL tạm để tránh rò rỉ bộ nhớ
  }

  checkboxAgreeValidator = (control: FormControl) => {
    const value = control.value;
    if (Array.isArray(value) && value.includes(true)) {
      return null;
    }
    return { shouldBeTrue: true };
  };

  initFormBuilder() {
    this.contractAgreementForm = this.formBuilder.group({
      fullName: [''],
      selectedRule: [null],
      email: [null],
      checkboxAgree: [true],
      identityNumber: [null],
      taxNumber: [null],
      issueDate: [null],
      expiredDate: [null],
      propertyName: [null],
      legalName: [null],
      description: [null],
    });
    this.selectedRuleModel =
      this.contractAgreementForm.get('selectedRule')!.value;
  }

  addClassActiveRule(rule: Rule, selectedRuleModel: Rule) {
    return {
      '!text-palette-blue-600': rule === selectedRuleModel,
      [rule.icon]: true,
    };
  }

  get f(): { [key: string]: AbstractControl } {
    return this.contractAgreementForm.controls;
  }

  showValidationErrors(form: FormGroup) {
    for (const controlName in form.controls) {
      if (form.controls.hasOwnProperty(controlName)) {
        form.get(controlName)!.markAsTouched();
      }
    }
  }

  renderValueContractForm(data: any) {
    const hasBusinessRole = !!data.business_role;
    const hasValidPersonalRole = !!data.personal_role;
    const selectedRule = hasBusinessRole
      ? this.rules[0]
      : hasValidPersonalRole
      ? this.rules[1]
      : null;

    this.contractAgreementForm.patchValue({
      fullName: data.name ?? '',
      selectedRule,
      email: data.email ?? '',
      checkboxAgree: [true],
      identityNumber: data.identity_number ?? '',
      taxNumber: data.tax_number ?? '',
      issueDate: data.issue_date ? new Date(data.issue_date) : null,
      expiredDate: data.expiry_date ? new Date(data.expiry_date) : null,
      legalName: data.legal_entity_name ?? '',
      propertyName: data.property_name ?? '',
      description: data.note ?? '',
    });

    this.selectedRuleModel =
      this.contractAgreementForm.get('selectedRule')!.value;
  }
  renderAttachment(data: any) {
    const attachments =
      data.business_role?.business_license_list ??
      data.personal_role?.addition_attachments ??
      [];

    this.draggedFiles = attachments.map((item: any) => ({
      file: {
        name: item.file_name,
        size: item.size || null,
        type: null,
        extension: item.file_extension,
      },
      url: '',
      isUpload: true,
      id: item.file_id,
    }));
  }
}
